import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import {
  Search,
  Edit,
  Delete,
  Add,
  Assignment,
  Visibility,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'https://smartvidesa.lk/api';

function AdminExams() {
  const [exams, setExams] = useState([]);
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [filteredExams, setFilteredExams] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    type: 'midterm',
    grade: '',
    exam_date: '',
    description: '',
    subjects: [],
  });

  const [examSubjects, setExamSubjects] = useState([
    { subject_name: '', total_marks: 100, passing_marks: 35 },
  ]);

  useEffect(() => {
    fetchExams();
    fetchClasses();
    fetchSubjects();
  }, []);

  useEffect(() => {
    const filtered = exams.filter(
      (exam) =>
        exam.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        exam.type?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredExams(filtered);
  }, [searchTerm, exams]);

  const fetchExams = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/exams`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('Fetched exams:', response.data);
      setExams(response.data);
      setFilteredExams(response.data);
    } catch (error) {
      console.error('Error fetching exams:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClasses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/classes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setClasses(response.data);
    } catch (error) {
    }
  };

  const fetchSubjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/subjects`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSubjects(response.data);
    } catch (error) {
    }
  };

  const handleOpenDialog = (exam = null) => {
    if (exam) {
      setSelectedExam(exam);
      setFormData({
        name: exam.name,
        type: exam.type,
        grade: exam.grade,
        exam_date: exam.exam_date,
        description: exam.description || '',
        subjects: [],
      });
    } else {
      setSelectedExam(null);
      setFormData({
        name: '',
        type: 'midterm',
        grade: '',
        exam_date: '',
        description: '',
        subjects: [],
      });
      setExamSubjects([{ subject_name: '', total_marks: 100, passing_marks: 35 }]);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedExam(null);
  };

  const handleAddSubject = () => {
    setExamSubjects([...examSubjects, { subject_name: '', total_marks: 100, passing_marks: 35 }]);
  };

  const handleRemoveSubject = (index) => {
    const updated = examSubjects.filter((_, i) => i !== index);
    setExamSubjects(updated);
  };

  const handleSubjectChange = (index, field, value) => {
    const updated = [...examSubjects];
    updated[index][field] = value;
    setExamSubjects(updated);
  };

  const handleSubmit = async () => {
    try {
      // Validate that all exam subjects have subject names
      const hasEmptySubjects = examSubjects.some(s => !s.subject_name);
      if (hasEmptySubjects) {
        alert('Please select a subject for all exam subjects');
        return;
      }

      const token = localStorage.getItem('token');
      const data = {
        ...formData,
        subjects: examSubjects,
      };

      if (selectedExam) {
        await axios.put(`${API_URL}/exams/${selectedExam.id}`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.post(`${API_URL}/exams`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      fetchExams();
      handleCloseDialog();
    } catch (error) {
      alert('Failed to save exam: ' + (error.response?.data?.message || error.message));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'completed':
        return 'default';
      case 'draft':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getTypeLabel = (type) => {
    const labels = {
      midterm: 'Mid Term',
      final: 'Final',
      quiz: 'Quiz',
      assignment: 'Assignment',
      other: 'Other',
    };
    return labels[type] || type;
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="xl">
        <Paper
          elevation={3}
          sx={{
            p: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          }}
        >
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Assignment sx={{ fontSize: 40, color: '#667eea', mr: 2 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, color: '#2d3748' }}>
                Exam Management
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<Add />}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)' },
              }}
              onClick={() => handleOpenDialog()}
            >
              Create New Exam
            </Button>
          </Box>

          {/* Search */}
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search exams..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />

          {/* Statistics */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={3}>
              <Paper sx={{ p: 2, bgcolor: '#e3f2fd', textAlign: 'center' }}>
                <Typography variant="h4" fontWeight="bold" color="primary">
                  {exams.length}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total Exams
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Paper sx={{ p: 2, bgcolor: '#e8f5e9', textAlign: 'center' }}>
                <Typography variant="h4" fontWeight="bold" sx={{ color: '#4caf50' }}>
                  {exams.filter((e) => e.status === 'active').length}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Active Exams
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Paper sx={{ p: 2, bgcolor: '#fff3e0', textAlign: 'center' }}>
                <Typography variant="h4" fontWeight="bold" sx={{ color: '#ff9800' }}>
                  {exams.filter((e) => e.status === 'draft').length}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Draft Exams
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={3}>
              <Paper sx={{ p: 2, bgcolor: '#f3e5f5', textAlign: 'center' }}>
                <Typography variant="h4" fontWeight="bold" color="secondary">
                  {exams.filter((e) => e.status === 'completed').length}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Completed
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          {/* Table */}
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><strong>Exam Name</strong></TableCell>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell><strong>Class</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell align="center"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredExams.map((exam) => (
                  <TableRow key={exam.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {exam.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={getTypeLabel(exam.type)} size="small" color="info" />
                    </TableCell>
                    <TableCell>
                      {exam.class ? `${exam.class.name} ${exam.class.section}` : 'N/A'}
                    </TableCell>
                    <TableCell>{exam.exam_date}</TableCell>
                    <TableCell>
                      <Chip
                        label={exam.status}
                        size="small"
                        color={getStatusColor(exam.status)}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <IconButton size="small" color="primary">
                        <Visibility />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() => handleOpenDialog(exam)}
                      >
                        <Edit />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Create/Edit Dialog */}
        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>
            {selectedExam ? 'Edit Exam' : 'Create New Exam'}
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Exam Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    label="Type"
                  >
                    <MenuItem value="midterm">Mid Term</MenuItem>
                    <MenuItem value="final">Final</MenuItem>
                    <MenuItem value="quiz">Quiz</MenuItem>
                    <MenuItem value="assignment">Assignment</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Grade</InputLabel>
                  <Select
                    value={formData.grade}
                    onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                    label="Grade"
                  >
                    {[6, 7, 8, 9, 10, 11].map((grade) => (
                      <MenuItem key={grade} value={grade}>
                        Grade {grade}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  type="date"
                  label="Exam Date"
                  value={formData.exam_date}
                  onChange={(e) => setFormData({ ...formData, exam_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </Grid>
              
              {!selectedExam && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Subjects
                    </Typography>
                  </Grid>
                  {examSubjects.map((examSubject, index) => (
                    <React.Fragment key={index}>
                      <Grid item xs={12} sm={5}>
                        <FormControl fullWidth>
                          <InputLabel>Subject</InputLabel>
                          <Select
                            value={examSubject.subject_name}
                            onChange={(e) => handleSubjectChange(index, 'subject_name', e.target.value)}
                            label="Subject"
                          >
                            {subjects.map((subject) => (
                              <MenuItem key={subject.id} value={subject.name}>
                                {subject.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={5} sm={3}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Total Marks"
                          value={examSubject.total_marks}
                          onChange={(e) => handleSubjectChange(index, 'total_marks', e.target.value)}
                        />
                      </Grid>
                      <Grid item xs={5} sm={3}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Passing Marks"
                          value={examSubject.passing_marks}
                          onChange={(e) =>
                            handleSubjectChange(index, 'passing_marks', e.target.value)
                          }
                        />
                      </Grid>
                      <Grid item xs={2} sm={1}>
                        <IconButton
                          color="error"
                          onClick={() => handleRemoveSubject(index)}
                          disabled={examSubjects.length === 1}
                        >
                          <Delete />
                        </IconButton>
                      </Grid>
                    </React.Fragment>
                  ))}
                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      startIcon={<Add />}
                      onClick={handleAddSubject}
                      fullWidth
                    >
                      Add Subject
                    </Button>
                  </Grid>
                </>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained">
              {selectedExam ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}

export default AdminExams;
